/* Progressive enhancement: all product content remains readable without JavaScript. */
if (!customElements.get('bpg-product-tabs')) {
  customElements.define('bpg-product-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.panels = Array.from(this.querySelectorAll('[data-bpg-panel]'));
      const list = this.querySelector('[data-bpg-tablist]');
      if (!list || !this.panels.length) return;
      this.ready = true;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-label', this.dataset.label || 'Product information');
      this.buttons = this.panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `${panel.id}-tab`;
        button.className = 'bpg-content__tab';
        button.textContent = panel.dataset.title;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.tabIndex = 0;
        list.append(button);
        button.addEventListener('click', () => this.activate(index), options);
        return button;
      });
      list.addEventListener('keydown', (event) => {
        const index = this.buttons.indexOf(event.target);
        if (index === -1) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
        if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.buttons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        next = (next + this.buttons.length) % this.buttons.length;
        this.activate(next);
        this.buttons[next].focus({ preventScroll: true });
        this.buttons[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, options);
      this.activate(0);
      this.classList.add('is-enhanced');
      list.hidden = false;
      const revealHash = (hash) => {
        if (!hash || hash === '#') return;
        let target;
        try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (_) { return; }
        const index = this.panels.findIndex((panel) => panel === target || panel.contains(target));
        if (index !== -1) this.activate(index);
      };
      revealHash(window.location.hash);
      window.addEventListener('hashchange', () => revealHash(window.location.hash), options);
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        const url = new URL(link.href, window.location.href);
        if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search) {
          revealHash(url.hash);
        }
      }, options);
    }

    disconnectedCallback() {
      if (!this.events) return;
      this.events.abort();
      this.buttons.forEach((button) => button.remove());
      this.panels.forEach((panel) => { panel.hidden = false; });
      this.querySelector('[data-bpg-tablist]').hidden = true;
      this.classList.remove('is-enhanced');
      this.ready = false;
    }

    activate(index) {
      this.buttons.forEach((button, current) => {
        const selected = current === index;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
        this.panels[current].hidden = !selected;
      });
      this.dispatchEvent(new CustomEvent('bpg:tab-change', { bubbles: true, detail: { panel: this.panels[index] } }));
    }
  });
}
